import { useState } from 'react'
import { useFlashToast } from '../../../hooks/useFlashToast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faIdCard, faCircleCheck, faCircleXmark, faHourglassHalf, faXmark,
  faChevronRight, faCalendar, faUser, faShieldHalved, faFileLines,
  faMagnifyingGlass, faInbox, faFilter
} from '@fortawesome/free-solid-svg-icons'
import AdminSidebar from '../../../components/admin/Sidebar'
import AdminTopbar from '../../../components/admin/Topbar'

const IDENTITY_STATUS_CONFIG = {
  pending: {
    label: 'Menunggu',
    icon: faHourglassHalf,
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    ring: 'ring-amber-200',
    dot: 'bg-amber-400',
  },
  approved: {
    label: 'Disetujui',
    icon: faCircleCheck,
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    ring: 'ring-emerald-200',
    dot: 'bg-emerald-400',
  },
  rejected: {
    label: 'Ditolak User',
    icon: faCircleXmark,
    bg: 'bg-red-50',
    text: 'text-red-600',
    ring: 'ring-red-200',
    dot: 'bg-red-400',
  },
  cancelled: {
    label: 'Dibatalkan Admin',
    icon: faXmark,
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    ring: 'ring-gray-200',
    dot: 'bg-gray-400',
  },
}

const REPORT_STATUS_CONFIG = {
  menunggu: { label: 'Menunggu',        bg: 'bg-gray-100',    text: 'text-gray-600' },
  diproses: { label: 'Sedang Diproses', bg: 'bg-amber-50',    text: 'text-amber-700' },
  selesai:  { label: 'Selesai',         bg: 'bg-emerald-50',  text: 'text-emerald-700' },
  ditolak:  { label: 'Ditolak',         bg: 'bg-red-50',      text: 'text-red-600' },
}

const CATEGORY_EMOJI = {
  'Korupsi & Gratifikasi': '🏛️', 'Penyalahgunaan Wewenang': '⚖️',
  'Konflik Kepentingan': '🔄', 'Penggelapan Aset Negara': '📦',
  'Suap & Pungli': '💰', 'Pemalsuan Dokumen': '📄',
  'Pelanggaran Kode Etik': '📋', 'Kecurangan Pengadaan': '🛒',
}

function IdentityStatusBadge({ status }) {
  const cfg = IDENTITY_STATUS_CONFIG[status] || IDENTITY_STATUS_CONFIG.pending
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${cfg.bg} ${cfg.text} ${cfg.ring}`}>
      <FontAwesomeIcon icon={cfg.icon} className="w-3 h-3" />
      {cfg.label}
    </span>
  )
}

const FILTER_TABS = [
  { key: '',          label: 'Semua' },
  { key: 'pending',   label: 'Menunggu' },
  { key: 'approved',  label: 'Disetujui' },
  { key: 'rejected',  label: 'Ditolak' },
  { key: 'cancelled', label: 'Dibatalkan' },
]

export default function AdminIdentityRequestsIndex({ current_user, requests }) {
  useFlashToast()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [filterStatus, setFilterStatus] = useState('')
  const [search, setSearch] = useState('')

  const filtered = requests.filter(r => {
    const matchStatus = !filterStatus || r.identity_status === filterStatus
    const q = search.toLowerCase()
    const matchSearch = !q ||
      r.tracking_code.toLowerCase().includes(q) ||
      r.title.toLowerCase().includes(q) ||
      (r.requested_by || '').toLowerCase().includes(q)
    return matchStatus && matchSearch
  })

  const counts = {
    '':          requests.length,
    pending:     requests.filter(r => r.identity_status === 'pending').length,
    approved:    requests.filter(r => r.identity_status === 'approved').length,
    rejected:    requests.filter(r => r.identity_status === 'rejected').length,
    cancelled:   requests.filter(r => r.identity_status === 'cancelled').length,
  }

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      <AdminSidebar current_user={current_user} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <AdminTopbar
          current_user={current_user}
          onMenuToggle={() => setSidebarOpen(true)}
          placeholder="Cari permintaan identitas..."
        >
          <div className="flex-1 max-w-md">
            <div className="relative">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Cari kode, judul, atau nama admin..."
                className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-gray-200 text-gray-700 placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none shadow-sm"
              />
            </div>
          </div>
        </AdminTopbar>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">

          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <FontAwesomeIcon icon={faIdCard} className="w-4.5 h-4.5 text-amber-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Permintaan Identitas Pelapor</h1>
                <p className="text-sm text-gray-500">Riwayat seluruh permintaan pembukaan identitas pelapor anonim</p>
              </div>
            </div>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {[
              { key: 'pending',   label: 'Menunggu',          color: 'amber',   icon: faHourglassHalf },
              { key: 'approved',  label: 'Disetujui',         color: 'emerald', icon: faCircleCheck },
              { key: 'rejected',  label: 'Ditolak User',      color: 'red',     icon: faCircleXmark },
              { key: 'cancelled', label: 'Dibatalkan Admin',  color: 'gray',    icon: faXmark },
            ].map(card => {
              const colorMap = {
                amber:   { bg: 'bg-amber-50',   text: 'text-amber-600',   icon: 'bg-amber-100' },
                emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', icon: 'bg-emerald-100' },
                red:     { bg: 'bg-red-50',     text: 'text-red-600',     icon: 'bg-red-100' },
                gray:    { bg: 'bg-gray-100',   text: 'text-gray-600',    icon: 'bg-gray-200' },
              }[card.color]
              return (
                <div
                  key={card.key}
                  onClick={() => setFilterStatus(filterStatus === card.key ? '' : card.key)}
                  className={`rounded-xl border p-4 flex items-center gap-3 cursor-pointer transition-all ${
                    filterStatus === card.key
                      ? `${colorMap.bg} border-current ring-2 ring-offset-1 ${colorMap.text.replace('text-', 'ring-')}/30`
                      : `${colorMap.bg} border-transparent hover:opacity-80`
                  }`}
                >
                  <div className={`w-9 h-9 ${colorMap.icon} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <FontAwesomeIcon icon={card.icon} className={`w-4 h-4 ${colorMap.text}`} />
                  </div>
                  <div>
                    <div className={`text-2xl font-bold ${colorMap.text}`}>{counts[card.key]}</div>
                    <div className="text-[11px] text-gray-500">{card.label}</div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Filter tabs */}
          <div className="flex flex-wrap gap-1 mb-4 bg-white border border-gray-200 rounded-xl p-1 w-fit shadow-sm">
            {FILTER_TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilterStatus(tab.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === tab.key
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {tab.label}
                <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
                  filterStatus === tab.key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                }`}>{counts[tab.key]}</span>
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                  <FontAwesomeIcon icon={faInbox} className="w-5 h-5 text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-500">Tidak ada permintaan identitas</p>
                <p className="text-xs text-gray-400 mt-1">
                  {search || filterStatus ? 'Coba ubah filter pencarian' : 'Belum ada permintaan yang dibuat'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-5 py-3">Laporan</th>
                      <th className="text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-5 py-3">Status Laporan</th>
                      <th className="text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-5 py-3">Diminta Oleh</th>
                      <th className="text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-5 py-3">Diminta Pada</th>
                      <th className="text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-5 py-3">Status Permintaan</th>
                      <th className="text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-5 py-3">Direspons Pada</th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map(req => {
                      const reportCfg = REPORT_STATUS_CONFIG[req.report_status] || REPORT_STATUS_CONFIG.menunggu
                      const idCfg = IDENTITY_STATUS_CONFIG[req.identity_status] || IDENTITY_STATUS_CONFIG.pending
                      return (
                        <tr
                          key={req.id}
                          className="hover:bg-gray-50 transition-colors cursor-pointer group"
                          onClick={() => { window.location.href = `/admin/reports/${req.id}` }}
                        >
                          {/* Laporan */}
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[11px] font-mono text-gray-400">{req.tracking_code}</span>
                              {req.anonymous && (
                                <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium">Anonim</span>
                              )}
                            </div>
                            <p className="text-sm font-semibold text-gray-900 line-clamp-1 group-hover:text-blue-700 transition-colors">{req.title}</p>
                            <p className="text-[11px] text-gray-400 mt-0.5">{CATEGORY_EMOJI[req.category]} {req.category}</p>
                          </td>

                          {/* Status Laporan */}
                          <td className="px-5 py-4">
                            <span className={`inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full ${reportCfg.bg} ${reportCfg.text}`}>
                              {reportCfg.label}
                            </span>
                          </td>

                          {/* Diminta Oleh */}
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <FontAwesomeIcon icon={faUser} className="w-3 h-3 text-blue-600" />
                              </div>
                              <span className="text-sm text-gray-700 font-medium">{req.requested_by || '—'}</span>
                            </div>
                          </td>

                          {/* Diminta Pada */}
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-1.5 text-sm text-gray-600">
                              <FontAwesomeIcon icon={faCalendar} className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              {req.requested_at || '—'}
                            </div>
                          </td>

                          {/* Status Permintaan */}
                          <td className="px-5 py-4">
                            <div className="flex flex-col gap-1.5">
                              <IdentityStatusBadge status={req.identity_status} />
                              {req.identity_status === 'approved' && (
                                <div className="flex items-center gap-1 text-[10px] text-emerald-600">
                                  <FontAwesomeIcon icon={faShieldHalved} className="w-3 h-3" />
                                  Perjanjian tersimpan
                                </div>
                              )}
                            </div>
                          </td>

                          {/* Direspons Pada */}
                          <td className="px-5 py-4">
                            {req.responded_at ? (
                              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                <FontAwesomeIcon icon={faCalendar} className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                {req.responded_at}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>

                          {/* Action */}
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-1 text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap text-xs font-medium">
                              Lihat Laporan
                              <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>

                {/* Footer count */}
                <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                  <p className="text-xs text-gray-400">
                    Menampilkan <span className="font-semibold text-gray-600">{filtered.length}</span> dari{' '}
                    <span className="font-semibold text-gray-600">{requests.length}</span> permintaan
                  </p>
                  <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
                    <FontAwesomeIcon icon={faFileLines} className="w-3 h-3" />
                    Data diambil dari seluruh laporan anonim
                  </div>
                </div>
              </div>
            )}
          </div>

        </main>
      </div>
    </div>
  )
}
